/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import 'semantic-ui-css/semantic.min.css';
import 'animate.css/animate.min.css';
import './App.css';

/*Import supported locales*/
import 'moment/locale/es';
import 'moment/locale/pt';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import React, { Component } from 'react';
import createBrowserHistory from 'history/createBrowserHistory';
import { Router, Prompt } from 'react-router-dom';
import { Dimmer, Loader, Responsive } from 'semantic-ui-react';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import find from 'lodash/find';

import ExpiredPayment from './components/expiredPayment';

import { Admin, AppBuilder, Contents, Dashboard, HelpCenter, Monetization, PlaylistManager, Login, Addons, AddApps, ManageApps, Cms } from './pages';
import merge from 'lodash/merge';
import _ from './translations';
import { ToastContainer } from 'react-toastify';
import pkg from '../package.json';

import { configure, toJS } from 'mobx';
import { Provider, inject, observer } from 'mobx-react';

import { RouterStore, syncHistoryWithStore } from 'mobx-react-router';
import { debug } from './utils';

import ErrorBoundary from './errorBoundary';
import { hotjar } from 'react-hotjar';

import {
    accessGroupsStore,
    appStore,
    categoriesStore,
    cinematicsStore,
    errorsStore,
    liveStore,
    imagesStore,
    newsStore,
    notificationsStore,
    playlistsStore,
    profileStore,
    providersStore,
    relationsStore,
    searchStore,
    settingsStore,
    siteMapStore,
    staffStore,
    tasksStore,
    typesStore,
    uiStore,
    usersStore,
    videosStore,
    vodStore,
    plansStore,
    stripeStore,
    templatesStore,
    ordersStore,
    productsStore,
    quotaStore,
    tutorialsStore,
    faqsStore,
    storeAppearanceStore,
    bannersStore,
    vastsStore,
    landingStore,
    filtersStore,
    productAddonStore,
    addonsStore,
    addonsProductsStore,
    addonProviderStore,
    customPlaylistStore,
    filesStore,
    manageAppsStore
} from './stores';

import dynamicComponent from './dynamicComponent';

import VersionCheck from './versionCheck';

const Navbar = dynamicComponent(() => import(/* webpackChunkName: "Navbar" */ './components/navbar'));

const Sidebar = dynamicComponent(() => import(/* webpackChunkName: "Sidebar" */ './components/sidebar'));

const browserHistory = createBrowserHistory();
const routingStore = new RouterStore();


const stores = {
    accessGroupsStore,
    appStore,
    categoriesStore,
    cinematicsStore,
    errorsStore,
    liveStore,
    imagesStore,
    newsStore,
    notificationsStore,
    playlistsStore,
    profileStore,
    providersStore,
    relationsStore,
    searchStore,
    settingsStore,
    siteMapStore,
    staffStore,
    tasksStore,
    typesStore,
    uiStore,
    usersStore,
    videosStore,
    vodStore,
    routingStore,
    plansStore,
    productsStore,
    templatesStore,
    stripeStore,
    ordersStore,
    quotaStore,
    tutorialsStore,
    faqsStore,
    storeAppearanceStore,
    bannersStore,
    vastsStore,
    landingStore,
    filtersStore,
    productAddonStore,
    addonsStore,
    addonsProductsStore,
    addonProviderStore,
    customPlaylistStore,
    filesStore,
    manageAppsStore
};

const history = syncHistoryWithStore(browserHistory, routingStore);

const clearStores = () => {
    forEach(stores, store => {
        if (store.reset) store.reset();
    });
};

const menus = [
    {
        name: _('Dashboard'),
        icon: 'dashboard',
        path: '/',
        matchUrl: '/',
    },
    {
        name: _('App Builder'),
        icon: 'app builder',
        matchUrl: '/app-builder/:section?',
        subItems: [
            {
                name: _('Color and Layout'),
                icon: 'grid layout',
                path: '/app-builder/layout',
            },
            {
                name: _('App Structure'),
                icon: 'sitemap',
                path: '/app-builder/site-map',
            },
            {
                name: _('Store Presence'),
                icon: 'shopping bag',
                path: '/app-builder/store-presence',
            },
            {
                name: _('Landing Page'),
                icon: 'shopping bag',
                path: '/app-builder/landing-page',
            },
        ],
    },
    {
        name: _('Playlist Manager'),
        icon: 'playlist manager',
        path: '/playlist-manager/list',
        matchUrl: '/playlist-manager/list',
    },
    {
        name: _('Content'),
        icon: 'content',
        matchUrl: '/contents/:section?/:param1?/:param2?',
        subItems: [
            {
                name: _('Content Manager'),
                icon: 'content',
                path: '/contents/content-manager/news/list',
                matchUrl: '/contents/content-manager/:param1?/:param2?',
            },
            {
                name: _('Media Library'),
                icon: 'file image outline',
                path: '/contents/media-library/images/list',
                matchUrl: '/contents/media-library/:param1?/:param2?',
            },
            {
                name: _('Access Groups'),
                path: '/contents/access-groups/list',
                matchUrl: '/contents/access-groups/:mode?/:id?',
            },
            {
                name: _('Categories'),
                path: '/contents/categories/list',
                matchUrl: '/contents/categories/:mode?/:id?',
            },
            {
                name: _('Types'),
                path: '/contents/types/list',
                matchUrl: '/contents/types/:mode?/:id?',
            },
            {
                name: _('Staff'),
                path: '/contents/staff/cast/list',
                matchUrl: '/contents/staff/:tab?/:mode?/:id?/:id3?',
            },
        ],
    },
    {
        name: _('Monetization'),
        icon: 'monetization',
        matchUrl: '/monetization/:subsection?/:mode?',
        subItems: [
            {
                name: _('Products'),
                icon: 'shop',
                path: '/monetization/products/products/list',
                matchUrl: '/monetization/products/:type/:mode?/:id?',
            },
            {
                name: _('Ads'),
                icon: 'bullhorn',
                path: '/monetization/ads/banner/list',
                matchUrl: '/monetization/ads/:type/:mode?/:id?',
            },
            {
                name: _('Products Add-ons'),
                icon: 'bullhorn',
                path: '/monetization/product-addons/list',
            },
        ],
    },
    {
        name: _('Admin'),
        icon: 'admin',
        path: '/administration/users/list',
        matchUrl: '/administration/:section?/:mode?',
    },
    {
        name: _('Add Apps'),
        icon: 'addons',
        path: '/addons/list',
        matchUrl: '/addons/:section?/:mode?',
    },
    {
        name: _('Addons'),
        icon: 'faq',
        matchUrl: '/plugins/:section?/:mode?/:param1?',
        subItems: [
            {
                name: _('Mux'),
                icon: 'talk',
                path: '/plugins/mux/list',
            },
        ],
    },
    {
        name: _('Landing Page CMS'),
        icon: 'landing',
        path: '/cms',
        matchUrl: '/cms',
    },
    {
        name: _('Help Center'),
        icon: 'faq',
        matchUrl: '/help-center/:section?/:subsection?/:mode?/:param1?/:param2?',
        subItems: [
            {
                name: _('FAQ'),
                icon: 'help',
                path: 'https://intercom.help/nunchee/en/collections/2364487-nunchee-menu-guide',
                external: true,
            },
            {
                name: _('Ask for Help'),
                icon: 'talk',
                path: '',
            },
        ],
    },
];

const theme = {
    background: '#f6f6f6',
    minWidth: '100%',
    minHeight: '100vh',
    overflow: 'hidden',
};

// For easier debugging
window._____APP_STATE_____ = stores;
configure({ enforceActions: 'always' });

class AppContainer extends Component {
    constructor(props) {
        super(props);
        debug(
            `%cRunning ${pkg.name || 'Fx-CM 2.0'} ${pkg.version || 'pre-release'} in ${process.env.NODE_ENV} mode`,
            'color: #00b7c0'
        );
    }

    componentDidMount(){
        const env = process.env.NODE_ENV;
        const packageVersion = process?.env?.npm_package_version || '0.0.1';

        // eslint-disable-next-line no-undef
        if(window.sentryDSN && Raven){    
            // eslint-disable-next-line no-undef
            Raven.config(window.sentryDSN, {
                release: packageVersion,
                environment: env || 'production',                 
                sampleRate: 1,
                debug: env === 'development',
                autoBreadcrumbs: {
                    'xhr': true,      // XMLHttpRequest
                    'console': true,  // console logging
                    'dom': true,       // DOM interactions, i.e. clicks/typing
                    'location': true, // url changes, including pushState/popState
                    'sentry': true     // sentry events
                }
            }).install()  
        }         
    }

    render() {
        return (
            <Provider {...stores}>
                <Router history={history}>
                    <App />
                </Router>
            </Provider>
        );
    }
}
@inject('profileStore', 'routingStore', 'uiStore', 'appStore', 'tasksStore', 'ordersStore', 'stripeStore', 'quotaStore')
@observer
class App extends Component {
    onUrlChange = () => {
        this.forceUpdate();
        window.Intercom('update', this.getIntercomData('simple'));
    };

    async getIntercomData(type = 'simple') {
        let intercomData = {};
        if (type === 'initial') {
            await this.props.quotaStore.getTraffic();
            await this.props.quotaStore.getQuota();
            await this.props.quotaStore.getTotalVideos();
            await this.props.ordersStore.list({
                hash: 'ordersList',
                params: {},
            });
            const activeOrder = this.getActiveOrder();
            if (profileStore && !isEmpty(profileStore) &&
                quotaStore && !isEmpty(quotaStore) &&
                ordersStore && !isEmpty(ordersStore)) {
                let profileJson = JSON.parse(JSON.stringify(profileStore));
                if (activeOrder) {
                    try {
                        const storageUsage = this.convertBToGb(quotaStore.quota.filter((el) => el.type === 'storage')[0].used);
                        const storageLimit = this.convertBToGb(quotaStore.quota.filter((el) => el.type === 'storage')[0].limit);
                        const storageUsagePercentage = parseFloat(((storageUsage / storageLimit) * 100).toFixed(2));
                        const streamingUsage = this.convertBToGb(quotaStore.quota.filter((el) => el.type === 'traffic')[0].used);
                        const streamingLimit = this.convertBToGb(quotaStore.quota.filter((el) => el.type === 'traffic')[0].limit);
                        const streamingUsagePercentage = parseFloat(((streamingUsage / streamingLimit) * 100).toFixed(2));
                        const totalVideos = quotaStore.totalVideos;
                        const totalUsers = quotaStore.quota.filter((el) => el.type === 'user')[0].used;
                        const planId = activeOrder.gateway.sku;
                        const planName = activeOrder.invoice.gateway.receipt.plan.nickname;
                        const userRole = profileJson.role;
                        const renewalDate = (Date.parse(activeOrder.expire) / 1000);
                        const planType = activeOrder.type;
                        const customFields = {
                            "storage_usage": storageUsage,
                            "storage_limit": storageLimit,
                            "storage_usage_percentage": storageUsagePercentage,
                            "streaming_usage": streamingUsage,
                            "streaming_limit": streamingLimit,
                            "streaming_usage_percentage": streamingUsagePercentage,
                            "total_videos": totalVideos,
                            "total_users": totalUsers,
                            "plan_name": planName,
                            "plan_id": planId,
                            "renewal_date_at": renewalDate,
                            "plan_type": planType
                        };
                        intercomData = {
                            app_id: "fp9hzm8k",
                            user_id: profileStore.session.user._id,
                            email: profileStore.profile.email,
                            user_role: userRole,
                            company: {
                                company_id: profileStore.client._id,
                                name: profileStore.client.name,
                                ...customFields
                            },
                        };
                    }
                    catch (err) {
                        intercomData = {
                            app_id: "fp9hzm8k",
                            user_id: profileStore.session.user._id,
                            email: profileStore.profile.email,
                            company: {
                                company_id: profileStore.client._id,
                                name: profileStore.client.name,
                            }
                        };
                    }
                };
            };
        } else {
            intercomData = {
                app_id: "fp9hzm8k",
                user_id: profileStore.session.user._id,
                email: profileStore.profile.email,
                company: {
                    company_id: profileStore.client._id,
                    name: profileStore.client.name,
                }
            };
        };
        return intercomData;
    };

    convertBToGb(byte) {
        return parseFloat((byte / 1e+9).toFixed(2));
    }

    getActiveOrder = () => {
        const collection = this.props.ordersStore.collection.get('ordersList') || {};
        let newCollection = toJS(collection.items || {});
        let activeOrder = find(newCollection, order => {
            if (
                get(order, 'status') === 1 ||
                get(order, 'status') === 8 ||
                get(order, 'status') === 16 ||
                (get(order, 'status') === 2 && new Date(get(order, 'expire')) > new Date())
            )
                return order;
        });

        return activeOrder;
    };

    async componentDidMount() {
        await this.props.profileStore.loadProfile();
        if (this.props.profileStore.isLoggedIn) {
            await this.props.appStore.getApp();
            await this.props.appStore.getApps();
            await this.props.tasksStore.getTasks();
        }
        if (window.production) hotjar.initialize(960873, 6);

        let href = window.location.href;
        // FIXME: mobx-react-router is not forcing the app to refresh on route change,
        // this interval checks for route changes every 100ms
        this._interval = setInterval(() => {
            if (window.location.href !== href) this.onUrlChange();
            href = window.location.href;
        }, 100);
    }

    async componentDidUpdate() {
        if (this.props.profileStore.isLoggedIn && !this.props.appStore.app._id) {
            await this.props.appStore.getApp();
            await this.props.appStore.getApps();
            await this.props.tasksStore.getTasks();
            const intercomSettings = await this.getIntercomData('initial')
            window.Intercom('update', intercomSettings);
        }
    }

    render() {
        const {
            isLoggedIn,
            profileIsLoading,
            client,
            isOrderExpired,
            lastOrder,
            customer,
        } = this.props.profileStore;

        //this is needed in order to call the location getter that does some reactive magic
        const { location } = this.props.routingStore; // eslint-disable-line no-unused-vars

        return (
            <div style={merge(theme, this.props.uiStore.theme.App)}>
                <ToastContainer />
                <Prompt
                    when={this.props.uiStore.navigationIsLocked}
                    message={_('You have unsaved changes. Are you sure you want to exit this page?')}
                />
                <If condition={process.env.NODE_ENV === 'production'}>
                    <VersionCheck />
                </If>

                <If condition={isLoggedIn && client._id}>
                    <Navbar
                        style={{
                            background: '#00b7c0',
                            borderRadius: '0px',
                            height: '70px',
                            margin: '0px',
                            position: 'fixed',
                            top: '0px',
                            left: '0px',
                            width: '100%',
                        }}
                    />
                </If>
                <Choose>
                    <When condition={isLoggedIn && client._id}>
                        <Choose>
                            <When condition={isOrderExpired}>
                                <ExpiredPayment
                                    lastOrder={lastOrder}
                                    customer={customer}
                                    stripeStore={this.props.stripeStore}
                                />
                            </When>
                            <Otherwise>
                                <ErrorBoundary>
                                    <Sidebar
                                        menus={menus}
                                        style={{
                                            marginTop: '70px',
                                            background: '#e7e8e7',
                                            width: '250px',
                                            minHeight: 'calc(100vh - 70px)',
                                        }}
                                    />
                                </ErrorBoundary>
                                <Responsive minWidth={768}>
                                    <style>
                                        {`
                                            .nunchee-cm-page {
                                            margin-top: 70px;
                                            width: calc(100% - 210px);
                                            min-height: 100vh;
                                            margin-left: 210px;
                                            }
                                        `}
                                    </style>
                                </Responsive>
                                <Responsive maxWidth={767}>
                                    <style>
                                        {`
                                            .nunchee-cm-page {
                                            margin-top: 70px;
                                            width: 100%;
                                            min-height: 100vh;
                                            overflow-x: auto;
                                            overflow-y: hidden;
                                            }
                                        `}
                                    </style>
                                </Responsive>
                                <div className="nunchee-cm-page">
                                    <ErrorBoundary>
                                        <Admin />
                                    </ErrorBoundary>
                                    <ErrorBoundary>
                                        <AppBuilder />
                                    </ErrorBoundary>
                                    <ErrorBoundary>
                                        <Contents />
                                    </ErrorBoundary>
                                    <ErrorBoundary>
                                        <Dashboard />
                                    </ErrorBoundary>
                                    <ErrorBoundary>
                                        <Monetization />
                                    </ErrorBoundary>
                                    <ErrorBoundary>
                                        <PlaylistManager />
                                    </ErrorBoundary>
                                    <ErrorBoundary>
                                        <Addons />
                                    </ErrorBoundary>
                                    <ErrorBoundary>
                                        <AddApps />
                                    </ErrorBoundary>
                                    <ErrorBoundary>
                                        <ManageApps />
                                    </ErrorBoundary>
                                    <ErrorBoundary>
                                        <Cms />
                                    </ErrorBoundary>
                                    <ErrorBoundary>
                                        <HelpCenter />
                                    </ErrorBoundary>
                                </div>
                            </Otherwise>
                        </Choose>
                    </When>
                    <Otherwise>
                        <Choose>
                            <When condition={!profileIsLoading}>
                                <ErrorBoundary>
                                    <Login />
                                </ErrorBoundary>
                            </When>
                            <Otherwise>
                                <Dimmer page active inverted>
                                    <Loader inverted>{_('Loading')}</Loader>
                                </Dimmer>
                            </Otherwise>
                        </Choose>
                    </Otherwise>
                </Choose>
            </div>
        );
    }
}

export default AppContainer;
export { clearStores };
